import React, { useState } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DialogProps } from "@material-ui/core/Dialog";
import { Theme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import Breakpoints from "common/breakpoints";
import { EMPTY_STRING } from "common/constants";
import ActionPopup from "components/ActionPopup";
import RightArrowIcon from "components/Icons/RightArrowIcon";
import SummaryInfo from "components/SummaryInfo";
import { useRacehorse360Api } from "hooks/api";
import { useInfiniteScrollToggle } from "hooks/useInfiniteScrollToggle";
import { useDebouncedNameValue } from "hooks/useSearch";
import { getStringMatchQueryField } from "utils/required-values-utils";
import { getTrainerFieldContentOrNumber } from "utils/trainer";
import TrainerSearch from "./TrainerSearch";

import useStyles from "./styles";

interface IProps extends DialogProps {
  onClose: () => void;
  onChangeTrainer: (horsePatch: racehorse360.IHorse) => void;
  selectedHorse: racehorse360.IHorse;
}

const MIN_CHARS_NUMBER_FOR_SEARCH = 3;

const ChangeTrainerPopup = (props: IProps) => {
  const { onClose, onChangeTrainer, selectedHorse } = props;

  const classes = useStyles();
  const matchesUpSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up(Breakpoints.SM_600)
  );

  useInfiniteScrollToggle();

  const [selectedTrainer, setSelectedTrainer] =
    useState<racehorse360.ITrainer>();
  const [searchQuery, setSearchQuery] = useState(EMPTY_STRING);

  const debouncedSearchQuery = useDebouncedNameValue(searchQuery, {
    timeout: 500,
    startFrom: MIN_CHARS_NUMBER_FOR_SEARCH
  });
  const stringMatchQuerySimilarOrContains = getStringMatchQueryField(
    "contains",
    debouncedSearchQuery
  );

  const { useListTrainers, useAssignHorseToTrainer } = useRacehorse360Api();

  const { mutateAsync: assignHorseToTrainer } = useAssignHorseToTrainer();

  const trainerOptions = [
    "firstName",
    "middleName",
    "lastName",
    "fullName",
    "trainerNumber",
    "type",
    matchesUpSm ? "horsesOnGround" : undefined
  ].filter(Boolean);

  const {
    data,
    isLoading: isListTrainersLoading,
    isFetching: isListTrainersFetching
  } = useListTrainers(
    {
      query: {
        fullName: stringMatchQuerySimilarOrContains
      },
      getOptions: {
        select: trainerOptions
      }
    },
    {
      enabled: Boolean(stringMatchQuerySimilarOrContains)
    }
  );

  const trainers = data?.trainers || [];

  const isLoading = [isListTrainersLoading, isListTrainersFetching].some(
    Boolean
  );

  const handleTrainerUnselect = () => {
    setSelectedTrainer(null);
  };

  const handleTrainerSelect = (item: racehorse360.ITrainer) => {
    setSelectedTrainer(item);
  };

  const handleChangeTrainer = async () => {
    return assignHorseToTrainer({
      horseId: selectedHorse.id,
      trainerId: selectedTrainer.id,
      getOptions: {
        select: [...trainerOptions.map(opt => `trainer.${opt}`)]
      }
    }).then(horse => {
      onChangeTrainer({ trainer: horse.trainer });
    });
  };

  const hasCurrentTrainer = Boolean(selectedTrainer);
  const defaultTrainerFullNameOrNumber = selectedHorse.trainer?.fullName
    ? getTrainerFieldContentOrNumber(
        selectedHorse.trainer.fullName,
        selectedHorse.trainer
      )
    : "No Trainer";
  const selectedTrainerFullNameOrNumber = getTrainerFieldContentOrNumber(
    selectedTrainer?.fullName,
    selectedTrainer
  );

  const summaryCardTitle = (
    <div className={classes.summaryTitleWrapper}>
      <span>{`${defaultTrainerFullNameOrNumber} `}</span>
      <RightArrowIcon className={classes.rightArrow} />
      <span>{` ${selectedTrainerFullNameOrNumber}`}</span>
    </div>
  );

  const successNotification = `${getTrainerFieldContentOrNumber(
    selectedTrainer?.fullName,
    selectedTrainer
  )} has been assigned to ${selectedHorse.name}`;

  const backOrCloseButtonText = hasCurrentTrainer ? "Back" : "Close";
  const backOrCloseButtonHandler = hasCurrentTrainer
    ? handleTrainerUnselect
    : onClose;

  return (
    <ActionPopup
      className={selectedTrainer && classes.changeTrainerPopup}
      open={true}
      onClose={null}
      title={`Change Trainer for ${selectedHorse.name}`}
      cancelButtonText={backOrCloseButtonText}
      submitButtonText={"Change Trainer"}
      onCancel={backOrCloseButtonHandler}
      onSubmit={handleChangeTrainer}
      disabledAction={Boolean(!selectedTrainer)}
      scrollable={Boolean(selectedTrainer)}
      notification={successNotification}
    >
      {!hasCurrentTrainer && (
        <TrainerSearch
          trainers={trainers}
          isDataLoading={isLoading}
          searchQuery={searchQuery}
          onChangeSearchQuery={setSearchQuery}
          onTrainerSelect={handleTrainerSelect}
          horseTrainerId={selectedHorse.trainer?.id}
          showHorsesOnGround={matchesUpSm}
        />
      )}

      {hasCurrentTrainer && (
        <SummaryInfo
          variant={"paper"}
          classes={{
            root: classes.horseSummaryRoot,
            info: classes.horseSummaryInfo
          }}
          selected={true}
          mainTitle={summaryCardTitle}
          contentNumber={selectedHorse.registrationNumber}
          contentText={selectedHorse.name}
          onItemUnselect={handleTrainerUnselect}
        />
      )}
    </ActionPopup>
  );
};

export default ChangeTrainerPopup;
